import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { identifyUser } from '../lib/analytics';
import { guestGetAllProgress, lsGet, lsSet } from '../lib/storage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = session check in progress

  async function migrateGuestProgress(prismaUserId) {
    const API = window.location.origin;
    const all = guestGetAllProgress();
    const entries = Object.values(all);
    if (!entries.length) return;

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? null;
    if (!token) return;

    // Fire-and-forget each progress entry — do not block sign-in
    entries.forEach(({ poemId, verseIdx }) => {
      fetch(`${API}/api/users/${prismaUserId}/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          poemId,
          status: verseIdx > 0 ? 'READING' : 'READING',
          currentVerse: verseIdx,
        }),
      }).catch(() => {});
    });

    // Clear guest progress after migration — server is now the source of truth
    try { localStorage.removeItem('diwanGuestProgress'); } catch { /* non-fatal */ }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u?.id) {
        identifyUser(u.id);
        localStorage.setItem('diwanPWACheckOnLogin', '1');
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'diwanPWACheckOnLogin',
          newValue: '1',
        }));
        // Re-hydrate diwanPrismaUserId if it was cleared (browser storage wipe,
        // new device, incognito, or sign-in from a screen other than Splash).
        // Without this, bookmark/save/feedback all silently fail until Splash is visited.
        if (!lsGet('diwanPrismaUserId') && session?.access_token) {
          try {
            const API = window.location.origin;
            const res = await fetch(`${API}/api/users`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({}),
            });
            if (res.ok) {
              const userData = await res.json();
              lsSet('diwanPrismaUserId', userData.id);
              if (userData.name)          lsSet('diwanDisplayName',   userData.name);
              if (userData.userLevel)     lsSet('diwanUserLevel',     userData.userLevel);
              if (userData.learningTrack) lsSet('diwanLearningTrack', userData.learningTrack);
              // Migrate any guest reading progress now that we have the correct Prisma ID
              migrateGuestProgress(userData.id).catch(() => {});
              // Notify any mounted screens that diwanPrismaUserId is now available
              window.dispatchEvent(new StorageEvent('storage', {
                key: 'diwanPrismaUserId',
                newValue: userData.id,
              }));
            }
          } catch { /* non-fatal — Splash flow is the primary path */ }
        } else if (lsGet('diwanPrismaUserId')) {
          // diwanPrismaUserId already present — migrate guest progress with correct Prisma ID
          migrateGuestProgress(lsGet('diwanPrismaUserId')).catch(() => {});
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { user: data?.user ?? null, error };
  }

  async function signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { user: data?.user ?? null, session: data?.session ?? null, error };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ user, signIn, signUp, signOut, loading: user === undefined }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
}
